<script lang="ts">
    import { setContext, untrack } from 'svelte'
    import { derived as sderived, writable, type Writable } from 'svelte/store'

    import * as yup from 'yup'

    import type { CrumbArray } from 'hydria/components/breadcrumb'
    import { validateSyncAt } from 'hydria/document_helpers'
    import { HydriaDocument } from 'hydria/HydriaDocument'
    import type { IDocument, IRevision } from 'hydria/HydriaDocument'
    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { spath } from 'hydria/svelte_helpers'

    import { Breadcrumb } from 'breadcrumb'

    import SimpleMarkdown from 'admin/field_components/SimpleMarkdown.svelte'
    // import SimpleMDE from 'simplemde'
    import DocumentEdit from 'hydria/components/DocumentEdit.svelte'
    import GenTextInput from 'hydria/components/Generics/GenTextInput.svelte'
    import LanguageSelector from 'hydria/components/Generics/LanguageSelector.svelte'

    interface Props {
        route: any
    }

    let { route }: Props = $props()

    interface ILocalDocument extends IDocument {}
    interface ILocalRevision extends IRevision {
        title: string
        content_mdown: string
        loctag: string
    }

    class LocalDocument extends HydriaDocument {
        docType = 'help'
        docTypePlural = 'helps'

        docTypeName = tgettext('Help text')
        docTypeNamePlural = tgettext('Help texts')
    }

    const Document = new LocalDocument()
    const status = sderived(Document.status, ($status) => $status)

    const revFields = ['title', 'content_mdown', 'lg', 'loctag']

    let inDocId = $derived(route.result.path.params?.docId)
    let inRevId = $derived(route.result.path.params?.revId)
    $effect(() => {
        inDocId
        inRevId
        untrack(() => {
            Document.reset(inDocId, inRevId)
        })
    })

    const revSaved = sderived(Document.revision, ($rev) => (!$rev ? $rev : Object.fromEntries(Object.entries($rev).filter(([k, v]) => revFields.includes(k)))) as ILocalRevision)
    const docSaved = sderived(Document.document, ($rev) => ({ ...$rev }) as ILocalDocument)

    // revEdit is actually handled in DocumentEdit.
    let revEdit: Writable<ILocalRevision | undefined> = writable(undefined)
    setContext('revEdit', revEdit)

    const revSchema = yup.object().shape({
        title: yup.string().required(),
        content_mdown: yup.string().required(),
        lg: yup
            .string()
            .required()
            .test('lgIsValid', '${path} is not valid', (value) => value === 'fr' || value === 'en'),
        loctag: yup
            .string()
            .required()
            .matches(/^[a-z0-9-]*$/),
    })
    let formIsValid = $derived(revSchema.isValidSync($revEdit))

    const crumbs = sderived(status, ($status) => {
        const c: CrumbArray = [{ txt: Document.docTypeNamePlural, url: spath(`/${Document.docTypePlural}`) }]
        switch ($status) {
            case 'loading':
                c.push({
                    txt: '…',
                })
                break
            case 'error':
            case 'not-found':
                c.push({
                    html: true,
                    txt: iconExclamationTriangle,
                })
                break
            case 'ready':
                c.push({
                    txt: tgettext('Edit'),
                })
                c.push({
                    txt: `<span class="text-muted">${Document.docId}</span>`,
                    html: true,
                })
        }
        return c
    })
</script>

<Breadcrumb {crumbs} />

<DocumentEdit {Document} docId={inDocId} revId={inRevId} {formIsValid} {revFields}>
    {#if $revEdit}
        <div class="card">
            <div class="card-header text-bg-primary">
                {$_('Document')}&#x2009;: {$_('Méta données')}
            </div>
            <div class="card-body d-grid gap-2">
                <GenTextInput label={tgettext('Titre')} saved={$revSaved.title} bind:value={$revEdit.title} isValid={(v) => validateSyncAt(revSchema, 'title', v)} helpText={tgettext('Ce texte est utilisé pour le titre de la page, dans la barre du navigateur.')} disabled={$status != 'ready'} />

                <LanguageSelector bind:value={$revEdit.lg} disabled={$status != 'ready'} />

                <GenTextInput label={tgettext('Jeton de lieu')} saved={$revSaved.loctag} bind:value={$revEdit.loctag} isValid={(v) => validateSyncAt(revSchema, 'loctag', v)} helpText={tgettext('Jeton (tag) utilisé dans le code pour référer à ce document.')} disabled={$status != 'ready'} />
            </div>
        </div>

        <div class="card">
            <div class="card-header text-bg-primary">
                {$_('Contenu')}
            </div>
            <div class="card-body d-grid gap-2">
                <SimpleMarkdown saved={$revSaved.content_mdown} bind:value={$revEdit.content_mdown} disabled={$status != 'ready'} label={tgettext('Contenu (Markdown)')} helpText={undefined} isValid={(v) => validateSyncAt(revSchema, 'content_mdown', v)} />
            </div>
        </div>
    {/if}
</DocumentEdit>
